import { LC_KEYS } from "./constants";

export type PreferredMic = { deviceId: string; groupId: string; label: string };

/** The microphone the user picked in the audio test, remembered across sessions. */
export const readPreferredMic = (): PreferredMic | null => {
  try {
    const raw = localStorage.getItem(LC_KEYS.PREFERRED_MIC);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.deviceId) return null;
    return { deviceId: parsed.deviceId, groupId: parsed.groupId ?? "", label: parsed.label ?? "" };
  } catch {
    return null;
  }
};

export const savePreferredMic = (device: { deviceId?: string; groupId?: string; label?: string }) => {
  if (!device?.deviceId) return;
  try {
    localStorage.setItem(
      LC_KEYS.PREFERRED_MIC,
      JSON.stringify({ deviceId: device.deviceId, groupId: device.groupId ?? "", label: device.label ?? "" }),
    );
  } catch {
    // Private mode / full storage — the choice just won't persist.
  }
};

/**
 * The stored mic if still connected, else null so the caller takes the browser default.
 * Never erased here — an unplugged headset would lose the preference for good.
 */
export const resolvePreferredMic = async (): Promise<PreferredMic | null> => {
  const stored = readPreferredMic();
  if (!stored) return null;
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const inputs = devices.filter((device) => device.kind === "audioinput" && device.deviceId);
    if (!inputs.length) return null;
    const match = inputs.find((device) => device.deviceId === stored.deviceId);
    return match ? { deviceId: match.deviceId, groupId: match.groupId, label: match.label } : null;
  } catch {
    return null;
  }
};
